/**
 * Layered procedural ride audio on a single (real-time or offline) context.
 *
 * Signal flow:
 *   layers → stems → ride bus ─┐
 *   rivals / ambience / fx / ui stems ──→ master → limiter → trim → destination
 *   every stem → biome send → convolver (A/B) → reverb return → master
 */
import type { CyclingDiscipline } from '../model';
import type { RideBiome } from '../biomes';
import { AmbienceScene } from './ambience';
import { Graph, Smooth } from './graph';
import { SoundKit } from './kit';
import { AirLayer, BrakeLayer, BreathLayer, DrivetrainLayer, RivalVoices, TyreLayer, WindLayer, type Frame } from './layers';
import { BIKE_ACOUSTICS, REVERB_PROFILES, ambienceDuck, brakeIntensity, clampTo, gateChimeNotes, landingIntensity, skidIntensity } from './mapping';
import type { BirdSpecies } from './samples';
import { OneShots, type PlayOptions } from './voices';
import type { AudioRideState, AudioTrack, RideCue, Stem } from './types';

export interface EngineOptions {
  /** Where the limiter output goes (defaults to ctx.destination). */
  destination?: AudioNode;
  /** Try HRTF panning for rivals (falls back to equal-power). */
  hrtf?: boolean;
  /** Pooled rival voices (3–4 recommended). */
  rivalVoices?: number;
  /** Seed for every generated buffer. */
  seed?: number;
}

const MASTER = .8;
const LOOKAHEAD = .16;
const STEMS: Stem[] = ['tyre', 'wind', 'drivetrain', 'brake', 'air', 'breath', 'rivals', 'ambience', 'fx', 'ui'];
const BIOME_BUFFERS: Record<RideBiome, (k: SoundKit) => (() => void)[]> = {
  alpine: k => [...birdJobs(k, ['finch', 'tit', 'robin']), ...[0, 1, 2].map(i => () => k.cowbell(i))],
  canyon: k => [...[0, 1, 2].map(i => () => k.pa(i)), () => k.hawk(0), () => k.hawk(1), () => k.cheer],
  coast: k => [0, 1, 2, 3].map(i => () => k.gull(i)),
  meadow: k => [...birdJobs(k, ['lark', 'finch', 'warbler']), () => k.bee(0), () => k.bee(1)],
  harbour: k => [...[0, 1, 2, 3].map(i => () => k.waterSlap(i)), ...[0, 1, 2].map(i => () => k.mast(i)), ...[0, 1, 2, 3].map(i => () => k.gull(i)), () => k.foghorn],
  park: k => [() => k.cheer, ...[0, 1, 2].map(i => () => k.pa(i))],
};
function birdJobs(k: SoundKit, species: BirdSpecies[]) { return species.flatMap(s => [0, 1, 2].map(i => () => k.bird(s, i))); }

export class RideAudioEngine {
  readonly ctx: BaseAudioContext;
  readonly kit: SoundKit;
  private master: GainNode;
  private stems = {} as Record<Stem, GainNode>;
  private sends = {} as Record<'ride' | 'fx' | 'ui' | 'ambience' | 'rivals', Smooth>;
  private sendBus: GainNode;
  private reverbReturn: GainNode;
  private convolvers: (ConvolverNode | null)[] = [null, null];
  private activeConvolver = 0;
  private ambDuck: Smooth;
  private shots: OneShots;
  private tyre: TyreLayer; private wind: WindLayer; private drive: DrivetrainLayer; private brake: BrakeLayer;
  private air: AirLayer; private breath: BreathLayer; private rivals: RivalVoices;
  private ambience: AmbienceScene | null = null;
  private frame: Frame = { active: false, speed: 0, surface: 'hardpack', discipline: 'mtb', airborne: false, airTime: 0, effort: 0, brake: 0, skid: 0, heartRate: 90, stamina: 100 };
  private state: AudioRideState | null = null;
  private biome: RideBiome | null = null;
  private discipline: CyclingDiscipline = 'mtb';
  private lastUpdate = 0; private lastStateAt = 0; private prevSpeed = 0; private decel = 0;
  private wasAirborne = false; private lastAirVelocity = 0; private lastCount = 0; private lastRemaining = 0;
  private ambienceInput: GainNode;
  private pebbleNext = 0; private variant = 0;

  constructor(ctx: BaseAudioContext, options: EngineOptions = {}) {
    this.ctx = ctx;
    this.kit = new SoundKit(ctx, options.seed ?? 7);
    const g = new Graph(ctx, this.kit);
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -9; limiter.knee.value = 4; limiter.ratio.value = 16; limiter.attack.value = .002; limiter.release.value = .2;
    const trim = g.gain(.85, options.destination ?? ctx.destination);
    limiter.connect(trim);
    // Sub-sonic cleanup keeps limiter headroom for what small speakers can play.
    this.master = g.gain(0, g.filter('highpass', 28, .7, limiter));
    const rideBus = g.gain(1, this.master);
    for (const stem of STEMS) this.stems[stem] = g.gain(1, ['tyre', 'wind', 'drivetrain', 'brake', 'air', 'breath'].includes(stem) ? rideBus : this.master);
    this.sendBus = g.gain(1);
    this.reverbReturn = g.gain(1, this.master);
    const send = (from: AudioNode) => { const s = g.gain(0, this.sendBus); from.connect(s); return new Smooth(ctx, s.gain, .3); };
    this.sends = { ride: send(rideBus), fx: send(this.stems.fx), ui: send(this.stems.ui), ambience: send(this.stems.ambience), rivals: send(this.stems.rivals) };
    const duck = g.gain(1, g.filter('highpass', 55, .7, this.stems.ambience));
    this.ambDuck = new Smooth(ctx, duck.gain, .6);
    this.ambienceInput = duck;
    this.shots = new OneShots(ctx);
    this.tyre = new TyreLayer(g, this.stems.tyre);
    this.wind = new WindLayer(g, this.stems.wind);
    this.drive = new DrivetrainLayer(g, this.stems.drivetrain);
    this.brake = new BrakeLayer(g, this.stems.brake);
    this.air = new AirLayer(g, this.stems.air);
    this.breath = new BreathLayer(g, this.stems.breath);
    this.rivals = new RivalVoices(g, this.stems.rivals, clampTo(options.rivalVoices ?? 4, 1, 6), options.hrtf ?? true);
    this.applyLayers();
    const k = this.kit;
    k.defer(() => k.tick, () => k.go, () => k.pop, () => k.thump, () => k.knock, () => k.exhale,
      ...[1, 2, 3, 4, 5, 6].map(streak => () => { const [a, b] = gateChimeNotes(streak); k.chime(a, b); }),
      ...[0, 1, 2, 3].map(i => () => k.pebble(i)), ...[0, 1, 2].map(i => () => k.slap(i)), ...[0, 1].map(i => () => k.chain(i)),
      ...[0, 1].map(i => () => k.swish(i)), ...[0, 1, 2].map(i => () => k.clatter(i)), ...[0, 1].map(i => () => k.thud(i)),
      ...[0, 1].map(i => () => k.scatter(i)), ...[0, 1].map(i => () => k.scrape(i)), () => k.spin(36), () => k.spin(54),
      () => k.fanfare, () => k.cheer);
  }

  /** Fade the whole mix in or out. */
  setActive(on: boolean) {
    this.master.gain.cancelScheduledValues(this.ctx.currentTime);
    this.master.gain.setTargetAtTime(on ? MASTER : 0, this.ctx.currentTime, on ? .12 : .06);
  }
  /** Switch ambience, reverb and bike acoustics for a ride or arena. */
  setBiome(track: AudioTrack | RideBiome) {
    if (typeof track !== 'string') this.discipline = track.discipline;
    const biome = typeof track === 'string' ? track : track.biome;
    if (biome === this.biome || !REVERB_PROFILES[biome]) return;
    this.biome = biome;
    this.ambience?.dispose();
    this.ambience = new AmbienceScene(this.ctx, this.kit, this.shots, this.ambienceInput, biome);
    this.kit.defer(...BIOME_BUFFERS[biome](this.kit));
    this.swapReverb(this.kit.impulse(biome));
    const p = REVERB_PROFILES[biome].sends;
    for (const key of Object.keys(this.sends) as (keyof typeof this.sends)[]) this.sends[key].set(p[key]);
  }
  private swapReverb(ir: AudioBuffer) {
    const next = this.activeConvolver ^ 1, prev = this.convolvers[this.activeConvolver];
    let conv = this.convolvers[next];
    try { if (!conv) throw new Error('new'); conv.buffer = ir; }
    catch { conv = this.ctx.createConvolver(); conv.buffer = ir; conv.connect(this.reverbReturn); this.convolvers[next] = conv; }
    if (prev) { try { this.sendBus.disconnect(prev); } catch { /* not connected */ } }
    this.sendBus.connect(conv);
    this.activeConvolver = next;
  }

  /**
   * Per-frame update. `null` = no rolling (menus, pause, results); the
   * ambience keeps breathing quietly underneath.
   */
  update(state: AudioRideState | null, track?: AudioTrack) {
    const t = this.ctx.currentTime;
    if (track) { this.discipline = track.discipline; if (track.biome !== this.biome) this.setBiome(track); }
    if (state !== this.state) { this.prevSpeed = state?.speed ?? 0; this.decel = 0; this.wasAirborne = false; this.lastUpdate = t; }
    this.state = state;
    const f = this.frame, dt = t - this.lastUpdate;
    f.discipline = this.discipline;
    if (!state) {
      f.active = false; f.speed = 0; f.airborne = false; f.brake = 0; f.skid = 0; f.effort = 0;
    } else {
      const crashing = state.crashTimer > 0;
      if (dt > .002) {
        const raw = (this.prevSpeed - state.speed) / dt;
        this.decel += ((!crashing && raw < 45 ? raw : 0) - this.decel) * Math.min(1, dt * 14);
        this.prevSpeed = state.speed; this.lastUpdate = t;
      }
      f.active = !crashing; f.speed = crashing ? 0 : state.speed; f.surface = state.surface;
      f.airborne = state.air > 0; f.airTime = state.airTime; f.effort = state.effort;
      f.brake = brakeIntensity(this.decel, state.speed); f.skid = skidIntensity(state.slip, state.drifting, state.speed);
      f.heartRate = state.heartRate; f.stamina = state.stamina;
      if (f.airborne) this.lastAirVelocity = state.airVelocity;
      this.lastStateAt = t;
    }
    this.applyLayers();
    this.wasAirborne = f.airborne;
    this.pump();
  }
  private applyLayers() {
    const f = this.frame;
    this.tyre.apply(f); this.wind.apply(f); this.drive.apply(f); this.brake.apply(f); this.air.apply(f); this.breath.apply(f);
    this.rivals.apply(this.state, f);
    this.ambDuck.set(this.state ? ambienceDuck(f.speed) : .5);
  }

  /** Schedule ambience and granular events ahead of the audio clock. Safe to call often. */
  pump() {
    const t = this.ctx.currentTime;
    if (this.state && t - this.lastStateAt > .5) { this.update(null); return; } // frames stopped: never leave tyres rolling
    this.ambience?.schedule(t, t + LOOKAHEAD);
    const level = this.tyre.crunchLevel, rate = level > .03 ? (2 + this.frame.speed * .9) * Math.min(1, level * 1.4) : 0;
    if (rate <= 0 || this.pebbleNext < t - .1) this.pebbleNext = t + .02;
    if (rate > 0) while (this.pebbleNext < t + LOOKAHEAD) {
      this.play(this.kit.pebble(this.variant++), this.stems.tyre, { at: this.pebbleNext, gain: (.1 + .25 * Math.random()) * Math.min(1, level), rate: .85 + Math.random() * .35, pan: Math.random() * .8 - .4, priority: 0 });
      this.pebbleNext += Math.max(.004, -Math.log(1 - Math.random() * .98) / rate);
    }
  }

  /** Countdown seconds remaining; plays a tick whenever the displayed number changes. */
  countdown(remaining: number) {
    const n = Math.ceil(remaining - 1e-3);
    if (remaining > this.lastRemaining + .5) this.lastCount = 0; // a new countdown started
    this.lastRemaining = remaining;
    if (n >= 1 && n <= 5 && n !== this.lastCount) this.cue('tick');
    this.lastCount = n;
  }

  cue(kind: RideCue) {
    const k = this.kit, t = this.ctx.currentTime, s = this.state, v = this.variant++;
    const fx = this.stems.fx, ui = this.stems.ui, jitter = () => .94 + Math.random() * .12;
    const loose = !!s && (s.surface === 'gravel' || s.surface === 'loam' || s.surface === 'hardpack');
    switch (kind) {
      case 'tick': this.play(k.tick, ui, { gain: .42, priority: 2 }); break;
      case 'go': this.lastCount = 0; this.play(k.go, ui, { gain: .5, priority: 2 }); break;
      case 'gate': { const [a, b] = gateChimeNotes(s?.flow ?? 1); this.play(k.chime(a, b), ui, { gain: .32, priority: 2 }); break; }
      case 'hit':
        this.play(k.knock, fx, { gain: .85, rate: jitter(), priority: 2 });
        this.play(k.chain(v), fx, { at: t + .01, gain: .3 });
        this.play(k.slap(v), fx, { gain: .3, rate: .8 });
        break;
      case 'jump':
        if (this.wasAirborne && s && s.air > .2) this.play(k.swish(v), fx, { gain: .45, rate: jitter(), pan: Math.random() * .6 - .3 });
        else { this.play(k.pop, fx, { gain: .5, rate: jitter() }); this.play(k.chain(v), fx, { at: t + .02, gain: .1 }); }
        break;
      case 'land': {
        const i = this.wasAirborne ? landingIntensity(this.lastAirVelocity) : .3;
        this.play(k.thump, fx, { gain: 1.05 * i, rate: jitter(), priority: 2 });
        this.play(k.slap(v), fx, { at: t + .004, gain: .7 * i, rate: jitter() });
        this.play(k.chain(v), fx, { at: t + .016, gain: .28 * i });
        if (loose) this.play(k.scatter(v), fx, { gain: .28 * i * (s!.surface === 'gravel' ? 1 : .5), rate: 1.15 });
        break;
      }
      case 'crash': {
        const c = clampTo((s?.crash?.speed ?? 10) / 14, .5, 1.3), side = Math.random() < .5 ? -1 : 1;
        this.play(loose ? k.scatter(v) : k.scrape(v), fx, { gain: .5 * c, priority: 2 });
        this.play(k.scrape(v + 1), fx, { gain: (loose ? .22 : .45) * c, rate: .8, priority: 2 });
        this.play(k.chain(v), fx, { at: t + .06, gain: .32 * c });
        this.play(k.clatter(v), fx, { at: t + .14, gain: .62 * c, pan: .3 * side, priority: 2 });
        this.play(k.spin(BIKE_ACOUSTICS[this.discipline].engagement), fx, { at: t + .42, gain: .32, pan: -.35 * side, priority: 2 });
        break;
      }
      case 'impact': {
        const c = clampTo((s?.crash?.speed ?? 10) / 14, .5, 1.3);
        this.play(k.thud(v), fx, { gain: .95 * c, priority: 2 });
        this.play(k.exhale, fx, { at: t + .02, gain: .2 });
        this.play(loose ? k.scatter(v + 1) : k.scrape(v + 1), fx, { at: t + .01, gain: .55 * c, priority: 2 });
        this.play(k.clatter(v + 1), fx, { at: t + .05, gain: .36 * c, rate: 1.08, pan: Math.random() * .6 - .3 });
        break;
      }
      case 'finish':
        this.play(k.fanfare, ui, { gain: .55, priority: 2 });
        this.play(k.cheer, fx, { at: t + .2, gain: .5, priority: 2 });
        break;
    }
  }
  private play(buffer: AudioBuffer, out: AudioNode, o: PlayOptions) { this.shots.play(buffer, out, o); }

  /** Diagnostics: hear only the listed stems (null restores everything). */
  solo(stems: Stem[] | null) {
    for (const stem of STEMS) this.stems[stem].gain.value = !stems || stems.includes(stem) ? 1 : 0;
  }
  get voices() { return this.shots.count; }
}
